import { toLowerCase } from 'Utils/string';
import { getStringProperty } from 'Utils/typeGuards';

const OWN_SOURCE_MARKER = 'convermax';

export const defaultGlobalErrorPolicy = Object.freeze({
  inlinePagePathSkips: ['/convermax/result/'],
  ignoredStackSubstrings: [] as readonly string[],
}) satisfies {
  inlinePagePathSkips: readonly string[];
  ignoredStackSubstrings: readonly string[];
};

type SourceEvidence = { matchField: string; matchedValue: string };

type GlobalErrorInput = {
  filename?: string;
  error?: unknown;
  policy?: typeof defaultGlobalErrorPolicy;
};

export function includesOwnSource(value: unknown): boolean {
  return toLowerCase(value).includes(OWN_SOURCE_MARKER);
}

export function getOwnErrorSourceEvidence({
  filename = '',
  error,
}: GlobalErrorInput = {}): SourceEvidence | null {
  const sourceCandidates: SourceEvidence[] = [
    { matchField: 'stack', matchedValue: getStringProperty(error, 'stack') ?? '' },
    { matchField: 'fileName', matchedValue: getStringProperty(error, 'fileName') ?? '' },
    { matchField: 'sourceURL', matchedValue: getStringProperty(error, 'sourceURL') ?? '' },
    { matchField: 'filename', matchedValue: filename },
  ];

  return sourceCandidates.find(({ matchedValue }) => includesOwnSource(matchedValue)) ?? null;
}

export function getGlobalErrorHandlingDecision({
  filename = '',
  error,
  policy = defaultGlobalErrorPolicy,
}: GlobalErrorInput = {}): {
  shouldHandle: boolean;
  dropReason: string | null;
  sourceEvidence: SourceEvidence | null;
} {
  const sourceEvidence = getOwnErrorSourceEvidence({ filename, error });
  if (!sourceEvidence) {
    return { shouldHandle: false, dropReason: 'not_own', sourceEvidence: null };
  }

  const effectiveSourceEvidence =
    getFilteredPageFallbackSourceEvidence({ error, policy, sourceEvidence }) ?? sourceEvidence;

  const dropReason = getFalsePositiveSuppressionReason({
    error,
    policy,
    sourceEvidence: effectiveSourceEvidence,
  });
  return { shouldHandle: !dropReason, dropReason, sourceEvidence: effectiveSourceEvidence };
}

function getFalsePositiveSuppressionReason({
  error,
  policy,
  sourceEvidence,
}: {
  error?: unknown;
  policy: typeof defaultGlobalErrorPolicy;
  sourceEvidence: SourceEvidence | null;
}): string | null {
  if (matchesAnySubstring(sourceEvidence?.matchedValue, policy.inlinePagePathSkips)) {
    return 'inline_page_path';
  }

  if (matchesAnySubstring(getStringProperty(error, 'stack'), policy.ignoredStackSubstrings)) {
    return 'stack_substring';
  }

  return null;
}

function getFilteredPageFallbackSourceEvidence({
  error,
  policy,
  sourceEvidence,
}: {
  error?: unknown;
  policy: typeof defaultGlobalErrorPolicy;
  sourceEvidence: SourceEvidence | null;
}): SourceEvidence | null {
  if (!matchesAnySubstring(sourceEvidence?.matchedValue, policy.inlinePagePathSkips)) {
    return null;
  }

  const assetUrl = findOwnedAssetUrlInStack(getStringProperty(error, 'stack'), policy.inlinePagePathSkips);
  return assetUrl ? { matchField: 'stack_asset_url', matchedValue: assetUrl } : null;
}

function findOwnedAssetUrlInStack(
  stack: string | undefined,
  inlinePagePathSkips: readonly string[],
): string | null {
  return (
    extractUrls(stack).find(
      (url) => isOwnedAssetUrl(url) && !matchesAnySubstring(url, inlinePagePathSkips),
    ) ?? null
  );
}

function extractUrls(value: unknown): string[] {
  if (typeof value !== 'string') {
    return [];
  }

  return (value.match(/https?:\/\/[^\s)]+/g) || []).map(stripLocationSuffix);
}

function stripLocationSuffix(url: string): string {
  return url.replace(/:\d+(?::\d+)?$/, '');
}

function isOwnedAssetUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return isOwnedHost(parsedUrl.hostname) && isScriptAssetPath(parsedUrl.pathname);
  } catch {
    return false;
  }
}

function isOwnedHost(hostname: string): boolean {
  const normalizedHostname = toLowerCase(hostname);
  return (
    normalizedHostname === 'client.convermax.com' ||
    normalizedHostname.endsWith('.myconvermax.com') ||
    normalizedHostname === 'localhost.convermax.dev'
  );
}

function isScriptAssetPath(pathname: string): boolean {
  return /\.js$/i.test(pathname);
}

function matchesAnySubstring(value: unknown, substrings: readonly string[] = []): boolean {
  const normalizedValue = toLowerCase(value);
  return substrings.some((substring) => normalizedValue.includes(toLowerCase(substring)));
}
